/* Leaft Blade V2 — buy box behaviour.
   INP budget 200 ms: selection and toggling only swap text and data attributes;
   rows, prices and both modes are all rendered server-side. The only network
   call on the page is add-to-cart (handled by Horizon's product-form-component). */

(() => {
  if (customElements.get('lb2-buy-box')) return;

  class LB2BuyBox extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.lb2-bb');
      this.rows = [...this.querySelectorAll('[data-lb2-tier]')];
      this.modeInputs = [...this.querySelectorAll('[data-lb2-mode-input]')];
      this.seriesInputs = [...this.querySelectorAll('[data-lb2-series-input]')];
      this.freqRow = this.querySelector('[data-lb2-freq]');
      this.variantInput = this.querySelector('[data-lb2-form-variant]');
      this.freqInput = this.querySelector('[data-lb2-form-freq]');
      this.planInput = this.querySelector('[data-lb2-form-plan]');
      this.ctaLabel = this.querySelector('[data-lb2-cta-label]');
      this.sticky = this.section ? this.section.querySelector('[data-lb2-sticky]') : null;
      this.stickyLine = this.section ? this.section.querySelector('[data-lb2-sticky-line]') : null;
      this.stickySub = this.section ? this.section.querySelector('[data-lb2-sticky-sub]') : null;
      this.cta = this.querySelector('.lb2-bb__cta');
      this.shipLine = this.querySelector('[data-lb2-ship-line]');
      // Elements whose text swaps with the series (badge, toggle subs).
      this.seriesTexts = [...this.querySelectorAll('[data-lb2-badge], [data-lb2-sub-save], [data-lb2-onetime-sub], [data-lb2-trial-body]')];

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-lb2-tier-input]')) this.update();
        if (e.target.matches('[data-lb2-mode-input]')) this.setMode(e.target.value);
        if (e.target.matches('[data-lb2-series-input]')) this.setSeries(e.target.value);
        if (e.target.matches('[data-lb2-freq]')) this.update();
      });

      // Option D. The grey series strip on phones switches series in one tap,
      // through the same radios the desktop cards use; focus moves to the
      // strip's other link so it is not lost when this one hides. The trial
      // line selects the one-off row and adds it to the cart.
      this.addEventListener('click', (e) => {
        const pick = e.target.closest('[data-lb2-series-pick]');
        if (pick) {
          const value = pick.dataset.lb2SeriesPick === 'b' ? 'b' : 'a';
          const input = this.seriesInputs.find((i) => i.value === value);
          if (input) input.checked = true;
          this.setSeries(value);
          const other = this.querySelector(`.lb2-bb__strip-${value} [data-lb2-series-pick]`);
          if (other) other.focus();
          return;
        }
        if (e.target.closest('[data-lb2-trial-add]')) this.addTrial();
      });

      // Deep links: ?variant_series=Pro selects the second series on load
      // (the "Drug-tested athlete?" links point here with that param), and
      // ?box=N selects that ladder row (the home freezer chips point here).
      const params = new URLSearchParams(window.location.search);
      if (this.seriesInputs.length && this.dataset.seriesB) {
        const want = params.get('variant_series');
        if (want && want.toLowerCase() === this.dataset.seriesB.toLowerCase()) {
          const input = this.seriesInputs.find((i) => i.value === 'b');
          if (input) input.checked = true;
          this.dataset.series = 'b';
        }
      }
      const box = params.get('box');
      if (box) {
        const row = this.rows.find((r) => r.dataset.blades === box);
        if (row) row.querySelector('input').checked = true;
      }
      // ?variant=ID is how a Google Shopping (or any catalogue) link names the
      // item that was clicked. Landing with Core preselected on a Pro link
      // showed the wrong price, so the series and the row follow the variant.
      // The mode is left alone: subscribing stays the default offer. A
      // variant with no row on this page (the 21 box) lands on the default.
      const variant = params.get('variant');
      if (variant) {
        const rowA = this.rows.find((r) => r.dataset.oneVariant === variant);
        const rowB = this.rows.find((r) => r.dataset.bOneVariant === variant);
        const row = rowA || rowB;
        if (row) {
          row.querySelector('input').checked = true;
          if (rowB && this.seriesInputs.length) {
            const input = this.seriesInputs.find((i) => i.value === 'b');
            if (input) input.checked = true;
            this.dataset.series = 'b';
          }
        }
      }

      this.initPackShot();
      this.initGallery();
      this.initSticky();
      this.initSeriesReveal();
      this.setSeries(this.series);
    }

    // Phones: the series cards may be folded behind one line (section
    // setting). Tapping the line shows the cards where they stand. A deep
    // link that lands on the second series shows them at once, so a selected
    // Pro is never out of sight.
    initSeriesReveal() {
      this.seriesReveal = this.querySelector('[data-lb2-series-reveal]');
      this.seriesToggle = this.querySelector('[data-lb2-series-collapsed]');
      if (!this.seriesReveal || !this.seriesToggle) return;
      this.seriesReveal.addEventListener('click', () => this.revealSeries());
      if (this.series === 'b') this.revealSeries();
    }

    revealSeries() {
      if (!this.seriesToggle) return;
      this.seriesToggle.classList.add('is-revealed');
      if (this.seriesReveal) {
        this.seriesReveal.classList.add('is-revealed');
        this.seriesReveal.setAttribute('aria-expanded', 'true');
      }
    }

    get mode() {
      return this.dataset.mode || 'sub';
    }

    get series() {
      return this.dataset.series === 'b' ? 'b' : 'a';
    }

    get selectedRow() {
      return this.rows.find((r) => r.querySelector('input').checked) || this.rows[0];
    }

    // All prices for a row in the active series, pre-formatted server-side.
    rowData(row) {
      const d = row.dataset;
      if (this.series === 'b' && d.bSubVariant) {
        return {
          subVariant: d.bSubVariant, oneVariant: d.bOneVariant,
          subPer: d.bSubPer, onePer: d.bOnePer,
          subBox: d.bSubBox, oneBox: d.bOneBox,
          subShip: d.bSubShip, oneShip: d.bOneShip,
          save: parseInt(d.bSave, 10) || 0,
          firstBox: d.bFirstBox || d.firstBox || '',
          firstShip: d.bFirstShip || d.firstShip || '',
        };
      }
      return {
        subVariant: d.subVariant, oneVariant: d.oneVariant,
        subPer: d.subPer, onePer: d.onePer,
        subBox: d.subBox, oneBox: d.oneBox,
        subShip: d.subShip, oneShip: d.oneShip,
        save: parseInt(d.save, 10) || 0,
        firstBox: d.firstBox || '',
        firstShip: d.firstShip || '',
      };
    }

    get maxSave() {
      return this.rows.reduce((m, row) => Math.max(m, this.rowData(row).save), 0);
    }

    get maxSaveBlades() {
      let best = 0, blades = '';
      this.rows.forEach((row) => {
        const save = this.rowData(row).save;
        if (save > best) { best = save; blades = row.dataset.blades; }
      });
      return blades;
    }

    setSeries(series) {
      this.dataset.series = series;
      // Badge, toggle subs and footnote numbers carry both series' text.
      this.seriesTexts.forEach((el) => {
        const text = series === 'b' ? el.dataset.b : el.dataset.a;
        if (text) el.textContent = text;
      });
      // The per Blade figure in the fair price copy appears inside this
      // component and again in the maths card further down the page, so it
      // follows the series document-wide rather than within the section.
      document.querySelectorAll('[data-lb2-fair-price]').forEach((el) => {
        const text = series === 'b' ? el.dataset.b : el.dataset.a;
        if (text) el.textContent = text;
      });
      this.setMode(this.mode);
    }

    setMode(mode) {
      this.dataset.mode = mode;
      // The toggle tells the truth whichever way the mode was reached.
      this.modeInputs.forEach((input) => { input.checked = input.value === mode; });
      // Subscribing with the one-off Starter pack selected cannot be added to
      // cart correctly, so the selection moves to the preselected box.
      if (mode === 'sub' && this.selectedRow?.dataset.oneTime === 'true') {
        const target = this.rows.find((r) => r.dataset.oneTime !== 'true' && r.dataset.blades === this.dataset.defaultBlades)
          || this.rows.find((r) => r.dataset.oneTime !== 'true');
        if (target) target.querySelector('input').checked = true;
      }
      // Per-row per-Blade price text swaps between the pre-formatted values.
      // The box total is the figure alone; the mode toggle and the cadence
      // select already say how it is bought.
      this.rows.forEach((row) => {
        const d = this.rowData(row);
        const per = row.querySelector('[data-lb2-row-per]');
        const was = row.querySelector('[data-lb2-row-was]');
        const saveTag = row.querySelector('[data-lb2-row-save]');
        const totalText = row.querySelector('[data-lb2-row-total-text]');
        // The trial row under the button carries its own wording and no
        // price cells, so it is skipped here. The option D tiles carry the
        // per Blade figure only; the box total moved into the purchase cards.
        if (per) {
          const onePrice = row.dataset.oneTime === 'true' || mode === 'onetime';
          per.textContent = onePrice ? d.onePer : d.subPer;
        }
        if (totalText) {
          if (row.dataset.oneTime === 'true') totalText.textContent = d.oneBox;
          else totalText.textContent = `${mode === 'onetime' ? d.oneBox : d.subBox} a box`;
        }
        // Pro's 7-box subscribes at the one-time price: no strike-through, no 0% tag.
        if (was) {
          was.textContent = d.onePer;
          was.style.display = d.save > 0 ? '' : 'none';
        }
        if (saveTag) {
          saveTag.textContent = `Save ${d.save}%`;
          saveTag.style.visibility = d.save > 0 ? '' : 'hidden';
        }
        // The first box price is the row's own subscription price less the
        // discount, rendered server side for each series. Buy once does not
        // carry the offer, so the tag goes with the mode.
        const firstTag = row.querySelector('[data-lb2-first-tag]');
        if (firstTag) {
          const label = this.dataset.firstTag || 'First box';
          firstTag.textContent = d.firstBox ? `${label} ${d.firstBox}` : '';
          firstTag.hidden = !d.firstBox || mode !== 'sub';
        }
      });
      this.update();
    }

    // The selling plan for the selected row at the selected cadence. Rows carry
    // a {weeks: planId} map rendered from the sub product's Recharge plans;
    // an empty result means this product has no plans and the form falls back
    // to the Delivery line-item property.
    selectedPlan(row) {
      if (!this.freqRow || !row.dataset.plans) return '';
      const weeks = this.freqRow.selectedOptions[0]?.dataset.weeks || '1';
      try {
        return JSON.parse(row.dataset.plans)[weeks] || '';
      } catch {
        return '';
      }
    }

    // How the selected box size lands at a given interval, stated honestly:
    // 28 days / 4 weeks is exactly one a day; 14 / 4 weeks is three to four
    // a week. Small counts are words, the way the rest of the page reads.
    cadence(blades, weeks) {
      const words = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
      const w = (n) => words[n] || String(n);
      const perWeek = blades / weeks;
      const perDay = perWeek / 7;
      if (Number.isInteger(perDay)) return `${w(perDay)} a day`;
      if (perDay > 1) return `${w(Math.floor(perDay))} to ${w(Math.ceil(perDay))} a day`;
      if (Number.isInteger(perWeek)) return `${w(perWeek)} a week`;
      return `${w(Math.floor(perWeek))} to ${w(Math.ceil(perWeek))} a week`;
    }

    // The cadence follows the box size at one Blade a day whenever the box
    // changes: 7 ships weekly, 14 every 2 weeks, 21 every 3, 28 every 4. The
    // shopper can still change it afterwards; the next box change resets it.
    // A box size without a matching interval leaves the selection alone.
    syncFreqToTier() {
      if (!this.freqRow) return;
      if (this.selectedRow?.dataset.oneTime === 'true') return;
      const blades = parseInt(this.selectedRow?.dataset.blades, 10);
      if (!blades || blades % 7 !== 0) return;
      const weeks = String(blades / 7);
      const match = [...this.freqRow.options].find((opt) => opt.dataset.weeks === weeks && !opt.disabled);
      if (match && !match.selected) this.freqRow.value = match.value;
    }

    // Only frequencies the selected box actually has a selling plan for are
    // offered. Without this, a frequency block with no matching plan would
    // fall through to the Delivery property and sell a one-time purchase that
    // the shopper believes is a subscription. A product with no plans at all
    // keeps every option (the documented fallback). Each hidden option is
    // logged once so the drift between theme and catalogue is visible.
    applyFreqAvailability(row) {
      if (!this.freqRow || !row) return;
      // A one-off row has no plans by design; the frequency row is hidden for it.
      if (row.dataset.oneTime === 'true') return;
      let plans = {};
      try {
        plans = JSON.parse(row.dataset.plans || '{}');
      } catch {
        plans = {};
      }
      const product = row.dataset.productTitle || `the ${row.dataset.blades}-Blade box`;
      const hasPlans = Object.keys(plans).length > 0;
      this.warned = this.warned || new Set();
      if (!hasPlans) {
        const key = `none:${product}`;
        if (!this.warned.has(key)) {
          this.warned.add(key);
          console.warn(`[lb2-buy-box] ${product} has no selling plans; subscribing falls back to the Delivery line-item property.`);
        }
      }
      [...this.freqRow.options].forEach((opt) => {
        const available = !hasPlans || Object.prototype.hasOwnProperty.call(plans, opt.dataset.weeks);
        opt.hidden = !available;
        opt.disabled = !available;
        if (!available) {
          const key = `${opt.dataset.weeks}:${product}`;
          if (!this.warned.has(key)) {
            this.warned.add(key);
            console.warn(`[lb2-buy-box] Frequency "${opt.value}" (${opt.dataset.weeks} weeks) hidden: ${product} has no matching selling plan.`);
          }
        }
      });
      const selected = this.freqRow.selectedOptions[0];
      if (!selected || selected.disabled) {
        const first = [...this.freqRow.options].find((opt) => !opt.disabled);
        if (first) this.freqRow.value = first.value;
      }
    }

    // The rate the shopper has chosen, written into the note line under the
    // select rather than into the option text. The box and the cadence are
    // already on screen, so the note is the rate alone: "One a day." A block
    // hint overrides the computed sentence.
    updateFreqNote() {
      if (!this.freqRow) return;
      const hint = this.querySelector('[data-lb2-freq-hint]');
      const row = this.selectedRow;
      const blades = parseInt(row?.dataset.blades, 10);
      const opt = this.freqRow.selectedOptions[0];
      if (!hint || !blades || !opt) return;
      const weeks = parseInt(opt.dataset.weeks, 10) || 1;
      const rate = this.cadence(blades, weeks);
      hint.textContent = opt.dataset.hint || `${rate.charAt(0).toUpperCase()}${rate.slice(1)}.`;
    }

    // The first box line in the Subscribe card, with the frequency picker
    // inside the sentence where the template's {cadence} token sits. It names
    // what is charged today and what is charged from then on, both read from
    // the box the subscription would use. On a box without an offer the text
    // hides and the picker stands alone; the line stays while Buy once is
    // selected, because it describes the Subscribe option.
    updateFirstBoxLine(d) {
      const line = this.querySelector('[data-lb2-first-line]');
      if (!line) return;
      const before = line.querySelector('[data-lb2-first-before]');
      const tail = line.querySelector('[data-lb2-first-tail]');
      const after = line.querySelector('[data-lb2-first-after]');
      const template = this.dataset.firstNote || '';
      const show = !!d.firstBox && template !== '';
      const [b = '', a = ''] = (show ? template.replace('{price}', d.subBox) : '').split('{cadence}');
      // The text after the last comma stays with the picker as one
      // unbreakable group (see the section), so the price and the cadence
      // never split across lines.
      const cut = b.lastIndexOf(', ');
      const head = cut >= 0 ? b.slice(0, cut + 1) : '';
      const rest = cut >= 0 ? b.slice(cut + 2) : b;
      if (before) { before.textContent = head; before.hidden = !show || head === ''; }
      if (tail) { tail.textContent = rest; tail.hidden = !show; }
      if (after) { after.textContent = a; after.hidden = !show; }
      line.classList.toggle('lb2-bb__buy-line--bare', !show);
      if (!this.freqRow) line.hidden = !show;
    }

    // Option D purchase cards. Subscribe leads with what checkout charges
    // today on the box a subscription would use (the first box price where
    // the row has one, else the box price); Buy once shows the selected
    // row's one-off box price. With the one-off trial row selected, the
    // Subscribe card describes the box subscribing would move to.
    updatePurchaseCards(row, d) {
      const subRow = row.dataset.oneTime === 'true' ? this.defaultSubRow() : row;
      const ds = subRow ? this.rowData(subRow) : d;
      this.updateFirstBoxLine(ds);
      const head = this.querySelector('[data-lb2-sub-head]');
      if (head) {
        const word = this.dataset.firstCtaWord || '';
        head.textContent = ds.firstBox ? `${ds.firstBox}${word ? ` ${word}` : ''}` : ds.subBox;
      }
      const once = this.querySelector('[data-lb2-once-price]');
      if (once) once.textContent = d.oneBox;
    }

    defaultSubRow() {
      return this.rows.find((r) => r.dataset.oneTime !== 'true' && r.dataset.blades === this.dataset.defaultBlades)
        || this.rows.find((r) => r.dataset.oneTime !== 'true');
    }

    // The trial line: select the one-off row (setMode follows it to Buy
    // once through update), then submit the same form the button does, so the
    // cart drawer behaves exactly as for Add to cart.
    addTrial() {
      const row = this.rows.find((r) => r.classList.contains('lb2-bb__trial-row'));
      const input = row && row.querySelector('[data-lb2-tier-input]');
      if (!input || !this.cta) return;
      input.checked = true;
      this.update();
      this.cta.click();
    }

    // The delivery promise, line one: this selection's shipping rate, then
    // the free-shipping box rule. The rate comes from the row's data (the NZ
    // rate steps up from $50.00, so it can differ by box, mode and series).
    updateShipLine(d, sub) {
      if (!this.shipLine) return;
      const lead = this.dataset.shipLead || '';
      const rule = this.dataset.freeRule || '';
      // A first box offer changes what shipping costs today: a halved box can
      // drop under the rate threshold, and the cart will charge the lower one.
      const rate = sub ? (d.firstBox && d.firstShip ? d.firstShip : d.subShip) : d.oneShip;
      // When the first box ships at a different rate from the renewals, both
      // are stated, so the renewal rate is not a surprise.
      const renewal = d.subShip;
      const twoRates = sub && d.firstBox && d.firstShip && renewal && rate !== 'free'
        && renewal !== 'free' && rate !== renewal;
      if (rate === 'free') {
        this.shipLine.textContent = `${lead} Free shipping on this box.`.trim();
      } else if (twoRates) {
        this.shipLine.textContent = `${lead} ${rate} on this first box, ${renewal} after.${rule ? ` ${rule}.` : ''}`.trim();
      } else if (rate) {
        this.shipLine.textContent = `${lead} ${rate} shipping on this box.${rule ? ` ${rule}.` : ''}`.trim();
      } else {
        this.shipLine.textContent = `${lead}${rule ? ` ${rule}.` : ''}`.trim();
      }
    }

    update() {
      const row = this.selectedRow;
      if (!row) return;
      const oneTime = row.dataset.oneTime === 'true';
      // Selecting the one-off row is a decision to buy once: the mode follows,
      // the frequency row goes with it, and setMode re-enters here.
      if (oneTime && this.mode === 'sub') {
        this.setMode('onetime');
        return;
      }
      this.applyFreqAvailability(row);
      if (this.lastRow !== row) { this.lastRow = row; this.syncFreqToTier(); }
      this.updateFreqNote();
      const d = this.rowData(row);
      const sub = this.mode === 'sub';
      const blades = row.dataset.blades;
      this.updatePurchaseCards(row, d);
      const box = sub ? d.subBox : d.oneBox;
      const per = sub ? d.subPer : d.onePer;
      const save = d.save;

      // The delivery promise follows the selection.
      this.updateShipLine(d, sub);

      // The pack render hangs off the main product's variant, so it follows the
      // one-time id in both modes: the subscription products carry no media.
      this.setPackShot(d.oneVariant);

      // Form state.
      this.variantInput.value = sub ? d.subVariant : d.oneVariant;
      const planId = sub ? String(this.selectedPlan(row)) : '';
      if (this.planInput) {
        this.planInput.disabled = planId === '';
        this.planInput.value = planId;
      }
      if (this.freqInput) {
        // With a real selling plan attached the plan itself carries the
        // cadence, so the Delivery property only submits as the fallback.
        this.freqInput.disabled = !sub || planId !== '';
        if (sub && this.freqRow) this.freqInput.value = this.freqRow.value;
      }

      // CTA label. Leads with the phrase shoppers scan a product page for;
      // the box total keeps the price honest next to the click.
      // The button has to equal the cart. With a first box offer the cart
      // charges the discounted price, so that is what the button says, with
      // the line under the ladder carrying the ongoing price.
      const firstBox = sub && d.firstBox ? d.firstBox : '';
      const charged = firstBox || box;
      const todayWord = this.dataset.firstCtaWord || '';
      this.ctaLabel.textContent = firstBox && todayWord
        ? `Add to cart · ${charged} ${todayWord}`
        : `Add to cart · ${charged}`;

      // Sticky bar mirrors the current selection in the ladder's own units.
      if (this.stickyLine) {
        const unit = this.dataset.sizeUnit || 'days';
        this.stickyLine.textContent = `${blades} ${unit} · ${per} a Blade`;
        if (oneTime) {
          this.stickySub.textContent = row.dataset.subLabel || 'Buy once';
        } else if (!sub) {
          this.stickySub.textContent = this.maxSave > 0
            ? `bought once · subscribe and save up to ${this.maxSave}%`
            : 'bought once';
        } else if (save <= 0) {
          // No saving on this box. Point at the box that does save, or, where
          // a market prices Buy once at the subscription price on every box,
          // say what the subscription is. Never "save 0%".
          this.stickySub.textContent = this.maxSave > 0
            ? `up to ${this.maxSave}% at ${this.maxSaveBlades} ${unit}`
            : 'skip, pause or cancel anytime';
        } else if (save >= this.maxSave) {
          this.stickySub.textContent = `save ${save}% · free shipping on this box`;
        } else {
          this.stickySub.textContent = `save ${save}% now · ${this.maxSave}% at ${this.maxSaveBlades} ${unit}`;
        }
      }
    }

    // Gallery carousel. The track's scroll position is the source of truth:
    // arrows and thumbs scroll it, and everything else reacts to where it
    // lands. That way a swipe, a click and a keypress all end up in the same
    // state without anyone having to keep a separate index in sync.
    // The first slide is the selected variant's pack render. The ladder and the
    // series switch pick variants without a native picker, so the swap is done
    // here from a map the section prints alongside the carousel.
    initPackShot() {
      const root = this.section;
      if (!root) return;
      const data = root.querySelector('[data-lb2-pack-map]');
      this.packSlide = root.querySelector('[data-lb2-pack] img');
      this.packThumb = root.querySelector('[data-lb2-pack-thumb] img');
      if (!data || !this.packSlide) return;
      try {
        this.packMap = JSON.parse(data.textContent);
      } catch (e) {
        this.packMap = null;
      }
    }

    setPackShot(variantId) {
      if (!this.packMap || !this.packSlide || !variantId) return;
      const key = String(variantId);
      // Keyed on the variant, not the src: two variants can legitimately share
      // an image while differing in alt text.
      if (this.packVariantId === key) return;
      const shot = this.packMap[key];
      if (!shot) return;
      this.packVariantId = key;
      this.packSlide.src = shot.src;
      if (shot.srcset) this.packSlide.srcset = shot.srcset;
      if (shot.alt) this.packSlide.alt = shot.alt;
      if (this.packThumb && shot.thumb) this.packThumb.src = shot.thumb;
    }

    initGallery() {
      const root = this.section;
      if (!root) return;
      const track = root.querySelector('[data-lb2-track]');
      if (!track) return;

      const slides = [...track.querySelectorAll('[data-lb2-slide]')];
      const thumbs = [...root.querySelectorAll('[data-lb2-thumb]')];
      const prev = root.querySelector('[data-lb2-prev]');
      const next = root.querySelector('[data-lb2-next]');
      if (slides.length < 2) return;

      let current = 0;

      const goTo = (i) => {
        const slide = slides[Math.max(0, Math.min(i, slides.length - 1))];
        if (slide) track.scrollTo({ left: slide.offsetLeft - track.offsetLeft });
      };

      const sync = (i) => {
        current = i;
        thumbs.forEach((btn) => {
          const on = Number(btn.dataset.lb2Thumb) === i;
          btn.classList.toggle('is-active', on);
          if (on) btn.setAttribute('aria-current', 'true');
          else btn.removeAttribute('aria-current');
        });
        // At either end the matching arrow has nothing to do, so it goes away
        // rather than sitting there dead.
        // Marked disabled rather than hidden: removing the element the shopper
        // has just focused drops focus to the top of the document.
        if (prev) prev.setAttribute('aria-disabled', String(i === 0));
        if (next) next.setAttribute('aria-disabled', String(i === slides.length - 1));
        // Media that scrolls away stops playing, and nothing off-screen holds
        // focus or gets read out.
        slides.forEach((s, n) => {
          if (n !== i) s.querySelectorAll('video, audio').forEach((m) => !m.paused && m.pause());
          s.inert = n !== i;
        });
        // With a dozen media the thumb row scrolls, so keep the current one in
        // view rather than leaving the shopper looking at an inactive strip.
        const active = thumbs[i];
        if (active && active.parentElement && active.parentElement.scrollWidth > active.parentElement.clientWidth) {
          const row = active.parentElement;
          const left = active.offsetLeft - (row.clientWidth - active.clientWidth) / 2;
          row.scrollTo({ left: Math.max(0, left) });
        }
      };

      thumbs.forEach((btn) => {
        btn.addEventListener('click', () => goTo(Number(btn.dataset.lb2Thumb)));
      });
      const step = (delta) => (e) => {
        if (e.currentTarget.getAttribute('aria-disabled') === 'true') return;
        goTo(current + delta);
      };
      if (prev) prev.addEventListener('click', step(-1));
      if (next) next.addEventListener('click', step(1));

      track.addEventListener('keydown', (e) => {
        // Only when the track itself has focus. Media curated on the product can
        // include a <video controls>, whose own arrow keys seek; keydown bubbles,
        // so without this guard the carousel would swallow them.
        if (e.target !== track) return;
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current + 1); }
      });

      // Whichever slide is most visible is the current one. An observer keeps
      // this correct through a swipe, a momentum scroll or a resize, none of
      // which fire a click.
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                sync(Number(entry.target.dataset.lb2Slide));
              }
            });
          },
          { root: track, threshold: [0.6] }
        );
        slides.forEach((s) => io.observe(s));
      }

      sync(0);
    }

    initSticky() {
      if (!this.sticky || !this.cta) return;
      // Pinned while the in-page CTA is off screen, so two add-to-cart
      // buttons are never on screen at the same time. With the "gallery"
      // setting it also waits for the photos to scroll off, so the bar is
      // there from the first screen of copy rather than after a scroll most
      // phone visitors never make.
      const media = this.section ? this.section.querySelector('.lb2-bb__media') : null;
      const fromGallery = this.dataset.stickyFrom === 'gallery' && media;
      let ctaVisible = true;
      let mediaVisible = fromGallery ? true : false;
      const apply = () => { this.sticky.hidden = ctaVisible || mediaVisible; };
      new IntersectionObserver(
        ([entry]) => { ctaVisible = entry.isIntersecting; apply(); },
        { rootMargin: '0px 0px -10px 0px' }
      ).observe(this.cta);
      if (fromGallery) {
        new IntersectionObserver(
          ([entry]) => { mediaVisible = entry.isIntersecting; apply(); },
          { rootMargin: '0px 0px 0px 0px', threshold: 0.15 }
        ).observe(media);
      }
    }

  }

  customElements.define('lb2-buy-box', LB2BuyBox);
})();
